import { useGetPersonAndAttributes } from 'hooks/useGetPersonAndAttributes';
import { useNavigate } from 'react-router-dom';
import {
  useGetParticipantTaskQuery,
  useUpdateParticipantTaskMutation,
} from 'store/api/pkd/endpoints/participantTask';
import { TaskStatus } from 'utils/enum';
import { getAttribute } from 'utils/participantTask';

const getButtonLabel = (status, hasLink, buttonName, readOnly) => {
  return status === TaskStatus.Inprogress
    ? 'Resume'
    : status === TaskStatus.Incomplete || hasLink
      ? buttonName
      : readOnly
        ? 'View'
        : 'Update';
};

const getButtonBackground = (status, title, readOnly) => {
  if (status === TaskStatus.Completed) {
    if (!readOnly) {
      return '#7FFAF6';
    }
    return '#3AA203';
  }

  return '#00778B';
};

const getButtonClickHandler =
  ({
    hasLink,
    status,
    updateParticipantTask,
    navigate,
    link,
    participantTaskId,
    refetchProfileParticipantTasks
  }) =>
    async () => {
      if (hasLink) {
        if (status === TaskStatus.Incomplete) {
          await updateParticipantTask({
            participantTaskId,
            entry  : { visited: true },
            status : TaskStatus.Completed,
          });

          refetchProfileParticipantTasks?.();
        }
        return !link.includes('https') && !link.includes('www')
          ? navigate(link)
          : window.open(link, '_blank');
      }
      return navigate(`/questionnaire/${participantTaskId}`);
    };

const useTaskCard = (participantTaskId, refetchProfileParticipantTasks) => {
  const navigate = useNavigate();

  const { personAccountProfileId, isPersonLoading } =
    useGetPersonAndAttributes();

  const {
    title,
    description,
    progress,
    completedAt,
    status,
    attributes,
    isParticipantTaskLoading,
  } = useGetParticipantTaskQuery(
    { personAccountProfileId, participantTaskId },
    {
      skip             : !personAccountProfileId,
      selectFromResult : ({ data, isFetching }) => ({
        title                    : data?.task?.title,
        description              : data?.task?.description,
        progress                 : data?.task?.progress,
        status                   : data?.task?.status,
        attributes               : data?.task?.attributes,
        participantTask          : data,
        isParticipantTaskLoading : isFetching,
        completedAt              : data?.taskEntries?.[0]?.updatedAt,
      }),
    }
  );

  const [updateParticipantTask, { isLoading: isSavingSurveyEntry }] =
    useUpdateParticipantTaskMutation();

  const meta = getAttribute(attributes, 'meta');

  const hasLink = meta?.isLink;
  const isInternalLink = meta?.isInternalLink;
  const link = meta?.linkTo;
  const readOnly = !meta?.editable;
  const buttonName = meta?.button_name ?? 'Start';
  const duration = meta?.duration;

  const buttonProps = {
    children   : getButtonLabel(status, hasLink, buttonName, readOnly),
    background : getButtonBackground(status, title, readOnly),
    onClick    : getButtonClickHandler({
      hasLink,
      status,
      updateParticipantTask,
      navigate,
      link,
      participantTaskId,
      refetchProfileParticipantTasks
    }),
  };

  const isLoading =
    isPersonLoading || isParticipantTaskLoading || isSavingSurveyEntry;

  return {
    title :
      title === 'Daily Survey' ? 'Answer our daily PULSE survey once' : title,
    description :
      title === 'DailySurvey'
        ? 'Answer short questions about your quality of life'
        : description,
    progress,
    completedAt,
    status,
    duration,
    buttonProps,
    isLoading,
  };
};

export default useTaskCard;
